import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowLeft, Shield } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, isAuthenticated, isLoading: authLoading, getDashboardRoute } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleAutoLogin = useCallback(async () => {
    setIsLoading(true);
    const result = await login('admin@fsf.com', 'password123');
    if (result.success) {
      const redirectUrl = result.redirect_url || getDashboardRoute();
      navigate(redirectUrl);
    } else {
      setError('Erreur de connexion automatique admin');
    }
    setIsLoading(false);
  }, [login, navigate, getDashboardRoute]);

  // Auto-login pour admin si paramètre présent
  useEffect(() => {
    const autoLogin = searchParams.get('auto');
    if (autoLogin === 'admin' && !isAuthenticated) {
      handleAutoLogin();
    }
  }, [searchParams, isAuthenticated, handleAutoLogin]);

  // Rediriger vers le dashboard si l'utilisateur est déjà connecté
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const dashboardRoute = getDashboardRoute();
      navigate(dashboardRoute);
    }
  }, [isAuthenticated, authLoading, navigate, getDashboardRoute]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      const redirectUrl = result.redirect_url || getDashboardRoute();
      navigate(redirectUrl);
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  // Afficher un écran de chargement si l'authentification est en cours
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fsf-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  // Ne pas afficher la page si l'utilisateur est déjà connecté (redirection en cours)
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header openContactModal={() => {}} />
      
      <div className="bg-gradient-to-br from-fsf-primary via-fsf-secondary to-fsf-marriage flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ minHeight: 'calc(100vh - 200px)' }}>
        <div className="max-w-md w-full space-y-8">
          {/* Retour à l'accueil */}
          <div className="text-center">
            <Link 
              to="/"
              className="inline-flex items-center text-white hover:text-fsf-warm transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour à l'accueil
            </Link>
          </div>

          {/* En-tête */}
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-white rounded-full flex items-center justify-center shadow-lg">
              <Shield className="h-8 w-8 text-fsf-primary" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-white">
              Espace utilisateur
            </h2>
            <p className="mt-2 text-sm text-fsf-light">
              Connectez-vous pour accéder au dashboard FSF
            </p>
          </div>

          {/* Formulaire de connexion */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="appearance-none relative block w-full pl-10 pr-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-fsf-primary focus:border-fsf-primary focus:z-10"
                    placeholder="admin@fsf.com"
                  />
                </div>
              </div>

              {/* Mot de passe */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="appearance-none relative block w-full pl-10 pr-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-fsf-primary focus:border-fsf-primary focus:z-10"
                    placeholder="Votre mot de passe"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Mot de passe oublié */}
              <div className="flex items-center justify-end">
                <div className="text-sm">
                  <Link 
                    to="/forgot-password"
                    className="font-medium text-fsf-primary hover:text-fsf-secondary transition-colors duration-200"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
              </div>

              {/* Bouton de connexion */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-fsf-primary to-fsf-secondary hover:from-fsf-secondary hover:to-fsf-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fsf-primary transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Connexion en cours...
                    </div>
                  ) : (
                    'Se connecter'
                  )}
                </button>
              </div>
            </form>

            {/* Informations de test */}
            {/* <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Comptes de test :</h4>
              <p className="text-xs text-blue-700">
                <strong>Admin :</strong> admin@fsf.com / password123
              </p>
            </div> */}
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LoginPage;